// ==================== CAMPAIGNS PAGE ====================
// WhatsApp Business API compliant campaign management

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Send,
  Pause,
  Play,
  XCircle,
  Edit2,
  Users,
  Clock,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Calendar,
  FileText,
  ChevronDown,
  RefreshCw,
  Info,
  X,
  Loader2,
  User,
} from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  Badge,
  Select,
  Textarea,
  ConfirmDialog,
  Card,
} from '../../../components/ui';
import { campaignsService } from '../../../services/campaigns.service';
import { templatesService } from '../../../services/templates.service';
import type { TemplateItem } from '../../../services/templates.service';
import { contactsService } from '../../../services/contacts.service';
import { useOrganizations } from '../../../hooks/useOrganizations';
import type { Contact } from '../../../types';
import type {
  Campaign,
  CampaignStatus,
  CampaignAudienceType,
  CreateCampaignData,
  CampaignStatsData,
} from '../../../services/campaigns.service';
import { staggerContainer, staggerItem } from '../../../utils/animations';

// ==================== HELPERS ====================

// Convert a Date to local datetime-local format string (YYYY-MM-DDTHH:mm)
const toLocalDatetimeString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// ==================== STATUS CONFIG ====================

const statusConfig: Record<CampaignStatus, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
  DRAFT: { color: 'text-gray-400', bgColor: 'bg-gray-500/10', icon: <FileText className="w-3.5 h-3.5" />, label: 'Draft' },
  SCHEDULED: { color: 'text-blue-400', bgColor: 'bg-blue-500/10', icon: <Clock className="w-3.5 h-3.5" />, label: 'Scheduled' },
  RUNNING: { color: 'text-green-400', bgColor: 'bg-green-500/10', icon: <Play className="w-3.5 h-3.5" />, label: 'Running' },
  PAUSED: { color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', icon: <Pause className="w-3.5 h-3.5" />, label: 'Paused' },
  COMPLETED: { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: 'Completed' },
  CANCELLED: { color: 'text-orange-400', bgColor: 'bg-orange-500/10', icon: <XCircle className="w-3.5 h-3.5" />, label: 'Cancelled' },
  FAILED: { color: 'text-red-400', bgColor: 'bg-red-500/10', icon: <AlertCircle className="w-3.5 h-3.5" />, label: 'Failed' },
};

const audienceTypeLabels: Record<CampaignAudienceType, string> = {
  ALL_CONTACTS: 'All Contacts',
  SELECTED_GROUPS: 'Selected Groups',
  TAG_FILTER: 'By Tags',
  MANUAL: 'Manual Selection',
};

// ==================== COMPONENTS ====================

const StatusBadge = ({ status }: { status: CampaignStatus }) => {
  const config = statusConfig[status] || statusConfig.DRAFT;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color} ${config.bgColor}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

const StatCard = ({ label, value, icon, color }: { label: string; value: number | string; icon: React.ReactNode; color: string }) => (
  <Card className="p-4" padding="none">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        {icon}
      </div>
    </div>
  </Card>
);

// ==================== MAIN COMPONENT ====================

const CampaignsPage = () => {
  const navigate = useNavigate();
  
  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<CampaignStatus | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [stats, setStats] = useState<CampaignStatsData | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Multiple organizations: the account chosen in the header scopes the campaigns and stats (all
  // accounts when none); a campaign is created and edited in that account, whose templates and
  // groups the form offers (tags and contacts are shared by all accounts)
  const { selectedOrganizationId, requireAccount, isMultiOrganization } = useOrganizations();

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    type: 'start' | 'pause' | 'cancel' | 'delete';
    campaign: Campaign | null;
  }>({
    isOpen: false,
    title: '',
    description: '',
    type: 'start',
    campaign: null,
  });

  // Form state
  const [formData, setFormData] = useState<CreateCampaignData>({
    name: '',
    description: '',
    templateId: '',
    scheduledAt: '',
    batchSize: 50,
    batchDelayMs: 2000,
    maxPerSecond: 40,
    testMode: false,
    testRecipients: [],
    audienceType: 'ALL_CONTACTS',
    groupIds: [],
    tagFilters: [],
    contactIds: [],
  });
  const [testRecipientInput, setTestRecipientInput] = useState('');

  // Manual contact selection state
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const contactSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ==================== DATA FETCHING ====================

  const fetchCampaigns = useCallback(async (page = currentPage) => {
    try {
      setLoading(true);
      const response = await campaignsService.getAll({
        search: searchQuery || undefined,
        status: filterStatus || undefined,
        organizationId: selectedOrganizationId,
        page,
        limit: pageSize,
      });
      setCampaigns(response.items);
      setTotalCampaigns(response.meta?.total || response.items.length);
      setTotalPages(response.meta?.totalPages || Math.ceil((response.meta?.total || response.items.length) / pageSize));
      setCurrentPage(response.meta?.page || page);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterStatus, selectedOrganizationId, currentPage, pageSize]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await templatesService.getAll({ status: 'APPROVED', limit: 100, organizationId: selectedOrganizationId });
      setTemplates(response.items);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, [selectedOrganizationId]);

  const fetchGroups = useCallback(async () => {
    try {
      // Fetch only active groups for campaign audience selection
      const response = await contactsService.getActiveGroups(selectedOrganizationId);
      setGroups(response.map((g) => ({ id: g.id, name: g.name })));
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  }, [selectedOrganizationId]);

  const fetchTags = useCallback(async () => {
    try {
      const tags = await contactsService.getAllTags();
      setAvailableTags(tags);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await campaignsService.getStats(selectedOrganizationId);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [selectedOrganizationId]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchTemplates();
    fetchGroups();
    fetchTags();
    fetchStats();
  }, [fetchTemplates, fetchGroups, fetchTags, fetchStats]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === '' || searchInput.length >= 3) {
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to first page on search
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, selectedOrganizationId]);

  // Contact search for manual selection
  const searchContacts = useCallback(async (query: string) => {
    if (query.length < 2) {
      setContactSearchResults([]);
      return;
    }

    setContactSearchLoading(true);
    try {
      const response = await contactsService.getAll({
        search: query,
        limit: 20,
        optedIn: true, // Only show opted-in contacts
      });
      // Filter out already selected contacts
      const selectedIds = new Set(selectedContacts.map(c => c.id));
      setContactSearchResults(response.items.filter(c => !selectedIds.has(c.id)));
    } catch (err) {
      console.error('Failed to search contacts:', err);
    } finally {
      setContactSearchLoading(false);
    }
  }, [selectedContacts]);

  // Debounced contact search
  useEffect(() => {
    if (contactSearchDebounceRef.current) {
      clearTimeout(contactSearchDebounceRef.current);
    }
    contactSearchDebounceRef.current = setTimeout(() => {
      searchContacts(contactSearchQuery);
    }, 300);
    return () => {
      if (contactSearchDebounceRef.current) {
        clearTimeout(contactSearchDebounceRef.current);
      }
    };
  }, [contactSearchQuery, searchContacts]);

  // Update formData.contactIds when selectedContacts changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      contactIds: selectedContacts.map(c => c.id),
    }));
  }, [selectedContacts]);

  // ==================== FORM HANDLERS ====================

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateId: '',
      scheduledAt: '',
      batchSize: 50,
      batchDelayMs: 2000,
      maxPerSecond: 40,
      testMode: false,
      testRecipients: [],
      audienceType: 'ALL_CONTACTS',
      groupIds: [],
      tagFilters: [],
      contactIds: [],
    });
    setTestRecipientInput('');
    setShowAdvanced(false);
    setSelectedContacts([]);
    setContactSearchQuery('');
    setContactSearchResults([]);
  };

  const handleOpenModal = async (campaign?: Campaign) => {
    if (campaign) {
      // Multiple organizations: the form's templates, groups and contacts come from one account
      if (!requireAccount('edit a campaign')) return;

      // Fetch full campaign details to get contactIds for MANUAL campaigns
      let fullCampaign = campaign;
      try {
        fullCampaign = await campaignsService.getOne(campaign.id);
      } catch (err) {
        console.error('Failed to fetch campaign details:', err);
        // Fall back to the campaign data we have
      }
      
      setEditingCampaign(fullCampaign);
      setFormData({
        name: fullCampaign.name,
        description: fullCampaign.description || '',
        templateId: fullCampaign.template?.id || '',
        scheduledAt: fullCampaign.scheduledAt ? toLocalDatetimeString(new Date(fullCampaign.scheduledAt)) : '',
        batchSize: fullCampaign.batchSize,
        batchDelayMs: fullCampaign.batchDelayMs,
        maxPerSecond: fullCampaign.maxPerSecond,
        testMode: fullCampaign.testMode,
        testRecipients: fullCampaign.testRecipients,
        audienceType: fullCampaign.audienceType,
        groupIds: fullCampaign.groupIds,
        tagFilters: fullCampaign.tagFilters,
      });
      
      // If MANUAL audience type, use contactIds from campaign and fetch contact details
      if (fullCampaign.audienceType === 'MANUAL' && fullCampaign.contactIds && fullCampaign.contactIds.length > 0) {
        try {
          // Fetch full contact details for the stored contact IDs using dedicated endpoint
          const contacts = await contactsService.getByIds(fullCampaign.contactIds);
          setSelectedContacts(contacts);
        } catch (err) {
          console.error('Failed to fetch campaign contacts:', err);
          // Fallback: create minimal contact objects from IDs
          const minimalContacts: Contact[] = fullCampaign.contactIds.map((id) => ({
            id,
            phoneNumber: '',
            name: `Contact ${id.slice(-6)}`,
            tags: [],
            customFields: {},
            optedIn: true,
            createdAt: '',
            updatedAt: '',
          }));
          setSelectedContacts(minimalContacts);
        }
      } else {
        setSelectedContacts([]);
      }
    } else {
      if (!requireAccount('create a campaign')) return;
      setEditingCampaign(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCampaign(null);
    setModalError(null);
    resetForm();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setModalError(null);

      // Convert local datetime-local value to ISO string (with timezone)
      // This ensures the server correctly interprets the user's intended time
      let scheduledAtISO: string | undefined;
      if (formData.scheduledAt) {
        // datetime-local gives us "2026-02-18T11:32" (local time without timezone)
        // Create a Date object which interprets it as local time, then convert to ISO
        const localDate = new Date(formData.scheduledAt);
        scheduledAtISO = localDate.toISOString();
      }

      const payload: CreateCampaignData = {
        ...formData,
        scheduledAt: scheduledAtISO,
      };

      if (editingCampaign) {
        await campaignsService.update(editingCampaign.id, payload);
      } else {
        await campaignsService.create({
          ...payload,
          ...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
        });
      }

      handleCloseModal();
      fetchCampaigns();
      fetchStats();
    } catch (err) {
      console.error('Failed to save campaign:', err);
      const error = err as { response?: { data?: { message?: string } } };
      setModalError(error.response?.data?.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  // ==================== CAMPAIGN ACTIONS ====================

  const handleStartCampaign = async (campaign: Campaign) => {
    const isTestMode = campaign.testMode && campaign.testRecipients?.length > 0;
    const recipientCount = isTestMode 
      ? campaign.testRecipients.length 
      : (campaign.stats.pending || campaign.stats.total);
    
    setConfirmDialog({
      isOpen: true,
      title: isTestMode ? 'Start Test Campaign' : 'Start Campaign',
      description: isTestMode
        ? `This is a TEST campaign. Messages will only be sent to ${recipientCount} test contact${recipientCount > 1 ? 's' : ''}: ${campaign.testRecipients.join(', ')}. After test completes, the campaign will return to Draft status.`
        : `Are you sure you want to start "${campaign.name}"? This will begin sending messages to ${recipientCount} recipients.`,
      type: 'start',
      campaign,
    });
  };

  const handlePauseCampaign = async (campaign: Campaign) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Pause Campaign',
      description: `Are you sure you want to pause "${campaign.name}"? You can resume it later.`,
      type: 'pause',
      campaign,
    });
  };

  const handleCancelCampaign = async (campaign: Campaign) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Campaign',
      description: `Are you sure you want to cancel "${campaign.name}"? This action cannot be undone. Pending messages will not be sent.`,
      type: 'cancel',
      campaign,
    });
  };

  const handleViewAnalytics = (campaign: Campaign) => {
    navigate(`/app/campaigns/${campaign.id}/analytics`);
  };

  const handleConfirmAction = async () => {
    const { type, campaign } = confirmDialog;
    if (!campaign) return;

    try {
      switch (type) {
        case 'start':
          await campaignsService.start(campaign.id);
          break;
        case 'pause':
          await campaignsService.pause(campaign.id);
          break;
        case 'cancel':
          await campaignsService.cancel(campaign.id);
          break;
      }
      fetchCampaigns();
      fetchStats();
    } catch (err) {
      console.error(`Failed to ${type} campaign:`, err);
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || `Failed to ${type} campaign`);
    }

    setConfirmDialog({ ...confirmDialog, isOpen: false, campaign: null });
  };

  // Test recipients management
  const addTestRecipient = () => {
    const phone = testRecipientInput.trim();
    if (phone && !formData.testRecipients?.includes(phone)) {
      setFormData({
        ...formData,
        testRecipients: [...(formData.testRecipients || []), phone],
      });
      setTestRecipientInput('');
    }
  };

  const removeTestRecipient = (phone: string) => {
    setFormData({
      ...formData,
      testRecipients: formData.testRecipients?.filter((p) => p !== phone) || [],
    });
  };

  // Tag management
  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tagFilters: formData.tagFilters?.filter((t) => t !== tag) || [],
    });
  };

  // Form validation
  const isFormValid = formData.name?.trim() && formData.templateId;

  // ==================== RENDER ====================

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Campaigns</h1>
          <p className="text-gray-400 text-sm mt-1">Create and manage WhatsApp marketing campaigns</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Create Campaign
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard
          label="Total"
          value={stats?.total ?? 0}
          icon={<BarChart3 className="w-5 h-5 text-white" />}
          color="bg-primary-600/20"
        />
        <StatCard
          label="Running"
          value={stats?.byStatus?.running ?? 0}
          icon={<Play className="w-5 h-5 text-green-400" />}
          color="bg-green-500/20"
        />
        <StatCard
          label="Scheduled"
          value={stats?.byStatus?.scheduled ?? 0}
          icon={<Clock className="w-5 h-5 text-blue-400" />}
          color="bg-blue-500/20"
        />
        <StatCard
          label="Completed"
          value={stats?.byStatus?.completed ?? 0}
          icon={<CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          color="bg-emerald-500/20"
        />
        <StatCard
          label="Messages Sent"
          value={(stats?.messageStats?.sent ?? 0).toLocaleString()}
          icon={<Send className="w-5 h-5 text-primary-400" />}
          color="bg-primary-500/20"
        />
        <StatCard
          label="Delivered"
          value={`${(stats?.messageStats?.totalRecipients ?? 0) > 0 ? Math.round(((stats?.messageStats?.delivered ?? 0) / (stats?.messageStats?.totalRecipients ?? 1)) * 100) : 0}%`}
          icon={<CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          color="bg-emerald-500/20"
        />
      </motion.div>

      {/* Filters */}
      <motion.div variants={staggerItem} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {/* Left side - Search */}
        <div className="relative max-w-xs w-full sm:w-auto">
          <Input
            placeholder="Search campaigns..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            leftIcon={<Search className="w-4 h-4 text-gray-400" />}
            className="text-sm"
          />
          {searchInput.length > 0 && searchInput.length < 3 && (
            <p className="absolute -bottom-5 left-0 text-[10px] text-yellow-500">Min 3 characters</p>
          )}
        </div>
        
        {/* Right side - Status & Refresh */}
        <div className="flex items-center gap-2">
          <div className="w-36">
            <Select
              value={filterStatus}
              onChange={(value) => setFilterStatus((Array.isArray(value) ? value[0] : value) as CampaignStatus | '')}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'DRAFT', label: 'Draft' },
                { value: 'SCHEDULED', label: 'Scheduled' },
                { value: 'RUNNING', label: 'Running' },
                { value: 'PAUSED', label: 'Paused' },
                { value: 'COMPLETED', label: 'Completed' },
                { value: 'CANCELLED', label: 'Cancelled' },
                { value: 'FAILED', label: 'Failed' },
              ]}
              size="sm"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => fetchCampaigns()} className="gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </Button>
        </div>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div variants={staggerItem} className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Campaign List */}
      <motion.div variants={staggerItem}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : campaigns.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-500/10 flex items-center justify-center">
              <Send className="w-8 h-8 text-primary-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">No campaigns yet</h3>
            <p className="text-gray-400 text-sm">Create your first campaign using the button above</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {campaigns.map((campaign) => (
              <Card
                key={campaign.id}
                className="p-4 hover:border-white/10 transition-colors"
                hoverable
                padding="none"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white truncate">{campaign.name}</h3>
                      <StatusBadge status={campaign.status} />
                      {campaign.testMode && (
                        <Badge variant="warning" className="text-xs">Test Mode</Badge>
                      )}
                    </div>
                    {campaign.description && (
                      <p className="text-sm text-gray-400 mb-3 line-clamp-1">{campaign.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" />
                        {campaign.template?.name || 'No template'}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        {campaign.testMode && campaign.testRecipients?.length ? (
                          <span className="text-yellow-400">
                            {campaign.testRecipients.length} test contact{campaign.testRecipients.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <>{campaign.stats.total.toLocaleString()} recipients</>
                        )}
                      </span>
                      {campaign.scheduledAt && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(campaign.scheduledAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {/* Show test recipients list when in test mode */}
                    {campaign.testMode && campaign.testRecipients && campaign.testRecipients.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {campaign.testRecipients.map((phone, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 text-yellow-400 rounded text-xs"
                          >
                            <User className="w-3 h-3" />
                            {phone}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="hidden md:flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-semibold text-white">{campaign.stats.sent}</p>
                      <p className="text-xs text-gray-500">Sent</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-emerald-400">{campaign.stats.delivered}</p>
                      <p className="text-xs text-gray-500">Delivered</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-blue-400">{campaign.stats.read}</p>
                      <p className="text-xs text-gray-500">Read</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-red-400">{campaign.stats.failed}</p>
                      <p className="text-xs text-gray-500">Failed</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {campaign.status === 'DRAFT' && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleOpenModal(campaign)}
                          className="gap-1.5"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleStartCampaign(campaign)}
                          className="gap-1.5"
                        >
                          <Play className="w-4 h-4" />
                          Start
                        </Button>
                      </>
                    )}
                    {campaign.status === 'SCHEDULED' && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleOpenModal(campaign)}
                          className="gap-1.5"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleStartCampaign(campaign)}
                          className="gap-1.5"
                        >
                          <Play className="w-4 h-4" />
                          Start Now
                        </Button>
                      </>
                    )}
                    {campaign.status === 'RUNNING' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePauseCampaign(campaign)}
                        className="gap-1.5"
                      >
                        <Pause className="w-4 h-4" />
                        Pause
                      </Button>
                    )}
                    {campaign.status === 'PAUSED' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStartCampaign(campaign)}
                          className="gap-1.5"
                        >
                          <Play className="w-4 h-4" />
                          Resume
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleCancelCampaign(campaign)}
                          className="gap-1.5"
                        >
                          <XCircle className="w-4 h-4" />
                          Cancel
                        </Button>
                      </>
                    )}
                    {['DRAFT', 'SCHEDULED', 'RUNNING', 'PAUSED'].includes(campaign.status) && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleCancelCampaign(campaign)}
                        className="gap-1.5"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Analytics button - hidden for test mode campaigns */}
                    {!campaign.testMode && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleViewAnalytics(campaign)}
                        className="gap-1.5"
                        title="View Analytics"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Error message for failed campaigns */}
                {campaign.lastError && campaign.status === 'FAILED' && (
                  <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-xs text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {campaign.lastError}
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalCampaigns > pageSize && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
            <p className="text-sm text-gray-400">
              Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCampaigns)} of {totalCampaigns} campaigns
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchCampaigns(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-400 px-3">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchCampaigns(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
        size="xl"
        contentClassName="p-6"
      >
        <div className="space-y-5">
          {/* Basic Info */}
          <div className="space-y-4">
            <Input
              label="Campaign Name *"
              placeholder="Enter campaign name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <Textarea
              label="Description"
              placeholder="Brief description of this campaign"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Template *</label>
            <Select
              value={formData.templateId}
              onChange={(value) => {
                const v = Array.isArray(value) ? value[0] : value;
                setFormData({ ...formData, templateId: v });
              }}
              options={[
                { value: '', label: 'Select an approved template' },
                ...templates.map((t) => ({
                  value: t.id,
                  label: `${t.name} (${t.category})`,
                })),
              ]}
              searchable
            />
            {templates.length === 0 && (
              <p className="text-xs text-yellow-500">
                No approved templates found. Please create and submit templates for approval first.
              </p>
            )}
          </div>

          {/* Audience Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-300">Audience</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(audienceTypeLabels).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData({ ...formData, audienceType: value as CampaignAudienceType })}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    formData.audienceType === value
                      ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                      : 'border-gray-700 bg-navy-200 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {isMultiOrganization && (formData.audienceType === 'ALL_CONTACTS' || formData.audienceType === 'TAG_FILTER') && (
              <p className="text-xs text-gray-500">Only contacts who have already chatted with this account.</p>
            )}

            {formData.audienceType === 'SELECTED_GROUPS' && (
              <div className="space-y-2">
                <Select
                  multiple
                  value={formData.groupIds || []}
                  onChange={(value) => {
                    const selected = Array.isArray(value) ? value : [value];
                    setFormData({ ...formData, groupIds: selected });
                  }}
                  options={groups.map((g) => ({
                    value: g.id,
                    label: g.name,
                  }))}
                  searchable
                  placeholder="Select groups..."
                />
                <p className="text-xs text-gray-500">Click to select multiple groups</p>
              </div>
            )}

            {formData.audienceType === 'TAG_FILTER' && (
              <div className="space-y-2">
                {availableTags.length > 0 ? (
                  <>
                    <Select
                      multiple
                      value={formData.tagFilters || []}
                      onChange={(values) => {
                        setFormData({
                          ...formData,
                          tagFilters: values as string[],
                        });
                      }}
                      options={availableTags
                        .filter((tag) => !formData.tagFilters?.includes(tag))
                        .map((tag) => ({
                          value: tag,
                          label: tag,
                        }))}
                      searchable
                      placeholder="Select tags..."
                    />
                    <p className="text-xs text-gray-500">Select from tags used in your contacts</p>
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-600 rounded-lg">
                    <p>No tags found in your contacts</p>
                    <p className="text-xs mt-1">Add tags to your contacts first</p>
                  </div>
                )}
                {formData.tagFilters && formData.tagFilters.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tagFilters.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-500/10 text-primary-400 rounded-full text-xs"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-primary-300"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {formData.audienceType === 'MANUAL' && (
              <div className="space-y-3">
                {/* Search Contacts */}
                <div className="relative">
                  <Input
                    placeholder="Search contacts by name or phone..."
                    value={contactSearchQuery}
                    onChange={(e) => setContactSearchQuery(e.target.value)}
                    leftIcon={<Search className="w-4 h-4 text-gray-400" />}
                  />
                  {contactSearchQuery.length > 0 && contactSearchQuery.length < 2 && (
                    <p className="text-[10px] text-yellow-500 mt-1">Enter at least 2 characters to search</p>
                  )}
                </div>

                {/* Search Results */}
                {contactSearchLoading && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 text-primary-400 animate-spin" />
                  </div>
                )}

                {!contactSearchLoading && contactSearchQuery.length >= 2 && contactSearchResults.length > 0 && (
                  <div className="bg-navy-200 border border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                    {contactSearchResults.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => {
                          setSelectedContacts(prev => [...prev, contact]);
                          setContactSearchResults(prev => prev.filter(c => c.id !== contact.id));
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left border-b border-gray-800 last:border-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-primary-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{contact.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-400">{contact.phoneNumber}</p>
                        </div>
                        <Plus className="w-4 h-4 text-primary-400" />
                      </button>
                    ))}
                  </div>
                )}

                {!contactSearchLoading && contactSearchQuery.length >= 2 && contactSearchResults.length === 0 && selectedContacts.length > 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">All matching contacts added</p>
                )}

                {!contactSearchLoading && contactSearchQuery.length >= 2 && contactSearchResults.length === 0 && selectedContacts.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No contacts found</p>
                )}

                {/* Selected Contacts */}
                {selectedContacts.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-300">
                        Selected Contacts ({selectedContacts.length})
                      </p>
                      <button
                        type="button"
                        onClick={() => setSelectedContacts([])}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-white/5 rounded-lg">
                      {selectedContacts.map((contact) => (
                        <span
                          key={contact.id}
                          className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 bg-primary-500/10 border border-primary-500/20 text-primary-400 rounded-full text-xs"
                        >
                          <span className="truncate max-w-[120px]">
                            {contact.name || contact.phoneNumber}
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedContacts(prev => prev.filter(c => c.id !== contact.id))}
                            className="hover:text-primary-300 shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedContacts.length === 0 && (
                  <p className="text-xs text-gray-500">Search and select contacts to add to this campaign</p>
                )}
              </div>
            )}
          </div>

          {/* Schedule */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Schedule (optional)
            </label>
            <Input
              type="datetime-local"
              value={formData.scheduledAt || ''}
              onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
            />
            <p className="text-xs text-gray-500">Leave empty to start manually</p>
          </div>

          {/* Test Mode */}
          <div className="space-y-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.testMode}
                onChange={(e) => setFormData({ ...formData, testMode: e.target.checked })}
                className="rounded border-gray-600 bg-navy-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-yellow-400">Test Mode</span>
            </label>
            <p className="text-xs text-gray-400">
              Send only to test recipients. Recommended before running a full campaign.
            </p>
            {formData.testMode && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter phone number"
                    value={testRecipientInput}
                    onChange={(e) => setTestRecipientInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTestRecipient())}
                  />
                  <Button type="button" variant="secondary" onClick={addTestRecipient}>
                    Add
                  </Button>
                </div>
                {formData.testRecipients && formData.testRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.testRecipients.map((phone) => (
                      <span
                        key={phone}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded-full text-xs"
                      >
                        {phone}
                        <button
                          type="button"
                          onClick={() => removeTestRecipient(phone)}
                          className="hover:text-yellow-300"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Advanced Settings */}
          <div className="border-t border-gray-800 pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              Advanced Settings
            </button>
            {showAdvanced && (
              <div className="mt-4 space-y-4 p-4 bg-navy-300 rounded-lg">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                    <div className="text-xs text-blue-300">
                      <p className="font-medium mb-1">WhatsApp API Rate Limits</p>
                      <p className="text-blue-400">
                        WhatsApp allows up to 80 messages per second. We recommend lower values to maintain
                        quality rating and avoid temporary blocks. Default settings are optimized for most use cases.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    type="number"
                    label="Batch Size"
                    value={formData.batchSize}
                    onChange={(e) => setFormData({ ...formData, batchSize: parseInt(e.target.value) || 50 })}
                    min={10}
                    max={100}
                  />
                  <Input
                    type="number"
                    label="Batch Delay (ms)"
                    value={formData.batchDelayMs}
                    onChange={(e) => setFormData({ ...formData, batchDelayMs: parseInt(e.target.value) || 2000 })}
                    min={1000}
                    max={10000}
                  />
                  <Input
                    type="number"
                    label="Max per Second"
                    value={formData.maxPerSecond}
                    onChange={(e) => setFormData({ ...formData, maxPerSecond: parseInt(e.target.value) || 40 })}
                    min={10}
                    max={80}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Default: 50 messages per batch, 2s delay between batches, max 40/second
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Error */}
        {modalError && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 mt-4">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{modalError}</p>
            <button onClick={() => setModalError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-700">
          <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid || saving}>
            {saving ? 'Saving...' : editingCampaign ? 'Update Campaign' : 'Create Campaign'}
          </Button>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={handleConfirmAction}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.type === 'delete' ? 'Delete' : confirmDialog.type === 'start' ? 'Start' : confirmDialog.type === 'pause' ? 'Pause' : confirmDialog.type === 'cancel' ? 'Yes' : 'Cancel'}
        variant={confirmDialog.type === 'delete' || confirmDialog.type === 'cancel' ? 'danger' : 'info'}
      />
    </motion.div>
  );
};

export default CampaignsPage;
